import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Search, Phone } from "lucide-react";

const tabs = [
  { key: "all", label: "All" },
  { key: "available", label: "Available" },
  { key: "booked", label: "Booked" },
  { key: "sold", label: "Sold" },
  { key: "delivered", label: "Delivered" },
];

const statusMeta = {
  available: "#16A34A",
  booked: "#D97706",
  sold: "#2563EB",
  delivered: "#475569",
};

export default function Inventory() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isOwner } = useAuth();

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/vehicles", { params: { status, q: q || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="a1-fade">
      <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1">Stock &amp; Search</h1>
      <p className="text-slate-500 font-semibold mb-5">Search by name, mobile, registration or model.</p>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
        <input
          data-testid="universal-search"
          className="a1-input !h-16 !text-xl"
          style={{ paddingLeft: "3.25rem" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type Kumar, MH12AB1234, Swift…"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`filter-${t.key}`}
            onClick={() => setStatus(t.key)}
            className={`px-5 py-2.5 rounded-xl font-bold whitespace-nowrap transition-colors ${
              status === t.key ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-400 font-semibold">Loading…</p>
      ) : items.length === 0 ? (
        <div className="a1-card p-10 text-center text-slate-400 font-semibold">No matching cars found.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((v) => (
            <button
              key={v.id}
              data-testid={`stock-row-${v.id}`}
              onClick={() => navigate(`/vehicle/${v.id}`)}
              className="a1-card p-4 sm:p-5 text-left hover:border-slate-300 transition-colors flex flex-wrap items-center gap-4 justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-head font-extrabold text-lg sm:text-xl tracking-tight">{v.model || "—"}</span>
                  <span className="num font-bold text-slate-500">{v.reg_no}</span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white capitalize" style={{ background: statusMeta[v.status] }}>
                    {v.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm font-semibold text-slate-500 mt-1 flex-wrap">
                  <span>{v.party_name || v.customer_name || "—"}</span>
                  {(v.party_mobile || v.customer_mobile) && (
                    <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{v.party_mobile || v.customer_mobile}</span>
                  )}
                  <span>Inward {formatDate(v.inward_date)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Purchase Rate</div>
                <div className="num font-head font-black text-xl tracking-tight">{formatINR(v.office_purchase_rate || v.total_buying_cost || v.purchase_price)}</div>
                {isOwner && (v.status === "sold" || v.status === "delivered") && v.profit !== undefined && (
                  <div className="mt-1" data-testid={`profit-${v.id}`}>
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Profit</span>
                    <div className="num font-black text-base" style={{ color: v.profit >= 0 ? "#16A34A" : "#EA580C" }}>{formatINR(v.profit)}</div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
